import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Unlock,
  Wrench,
  UserX,
  KeyRound,
  Clock,
  MapPin,
  Cpu,
  User as UserIcon,
  RefreshCw
} from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';
import { useSocket } from './SocketContext';
import { getTimeRemaining, isExpiryApproaching } from '../utils/timezone';

const LockerDrawerContext = createContext(null);

export const useLockerDrawer = () => {
  const ctx = useContext(LockerDrawerContext);
  if (!ctx) throw new Error('useLockerDrawer must be used within LockerDrawerProvider');
  return ctx;
};

/**
 * Global slide-over panel showing one locker's full picture with every
 * action staff need (unlock, extend, unassign, re-issue PIN, maintenance),
 * reachable from any page via openLocker(id). Kills the cross-page
 * navigation dance: Users -> Lockers -> Groups to answer one question.
 */
export const LockerDrawerProvider = ({ children }) => {
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [lockerId, setLockerId] = useState(null);
  const [locker, setLocker] = useState(null);
  const [occupant, setOccupant] = useState(null);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { title, message, tone, label, action }
  const [newPin, setNewPin] = useState(null);
  const [expiryHours, setExpiryHours] = useState(24);

  const openLocker = useCallback((id) => {
    setNewPin(null);
    setLockerId(id);
  }, []);

  const close = useCallback(() => {
    setLockerId(null);
    setLocker(null);
    setOccupant(null);
    setGroup(null);
    setConfirm(null);
    setNewPin(null);
  }, []);

  const refresh = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const [lockersRes, usersRes, groupsRes, expiryRes] = await Promise.all([
        axios.get('/api/lockers'),
        axios.get('/api/users'),
        axios.get('/api/groups'),
        axios.get('/api/settings/locker-expiry')
      ]);
      const found = lockersRes.data.find(l => l.id === id);
      setLocker(found || null);
      setOccupant(found?.user_id ? usersRes.data.find(u => u.id === found.user_id) || null : null);
      setGroup(groupsRes.data.find(g => g.locker_ids?.includes(id)) || null);
      setExpiryHours(expiryRes.data.lockerExpiryHours || 24);
      if (!found) toast.error('Locker not found');
    } catch (err) {
      console.error('Drawer fetch failed:', err);
      toast.error('Failed to load locker details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (lockerId) refresh(lockerId);
  }, [lockerId, refresh]);

  // Keep the open drawer live
  useEffect(() => {
    if (!socket || !lockerId) return undefined;
    const onLockerUpdated = (updated) => {
      if (updated.id === lockerId) {
        setLocker(updated);
        if (!updated.user_id) setOccupant(null);
      }
    };
    const onUserUpdated = (updated) => {
      setOccupant(prev => (prev && prev.id === updated.id ? updated : prev));
    };
    socket.on('locker-updated', onLockerUpdated);
    socket.on('user-updated', onUserUpdated);
    return () => {
      socket.off('locker-updated', onLockerUpdated);
      socket.off('user-updated', onUserUpdated);
    };
  }, [socket, lockerId]);

  // Esc closes
  useEffect(() => {
    if (!lockerId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lockerId, close]);

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await refresh(lockerId);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Action failed');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const actions = locker && {
    unlock: () =>
      setConfirm({
        title: `Open locker ${locker.name}?`,
        message: 'The latch pulses open for a moment.',
        tone: 'primary',
        label: 'Open it',
        action: () => run(
          () => axios.post(`/api/lockers/${locker.id}/command`, { command: 'openlock' }),
          `Locker ${locker.name} is opening`
        )
      }),
    maintenance: () => {
      const entering = locker.status !== 'maintenance';
      setConfirm({
        title: entering ? `Put ${locker.name} in maintenance?` : `Return ${locker.name} to service?`,
        message: entering ? 'The locker will stop accepting assignments.' : 'The locker becomes available again.',
        tone: 'warning',
        label: entering ? 'Maintenance' : 'Back in service',
        action: () => run(
          () => axios.post(`/api/lockers/${locker.id}/command`, { command: entering ? 'maintenance' : 'normal' }),
          entering ? 'Locker set to maintenance' : 'Locker back in service'
        )
      });
    },
    unassign: () =>
      setConfirm({
        title: `Unassign locker ${locker.name}?`,
        message: `${occupant ? `${occupant.first_name} ${occupant.last_name}` : 'The guest'} will lose access and the locker opens for clear-out.`,
        tone: 'danger',
        label: 'Unassign',
        action: () => run(
          () => axios.post(`/api/lockers/${locker.id}/unassign`),
          `Locker ${locker.name} unassigned`
        )
      }),
    reissuePin: () =>
      setConfirm({
        title: 'Issue a new PIN?',
        message: 'The old PIN stops working immediately. Show the new one to the guest.',
        tone: 'primary',
        label: 'New PIN',
        action: async () => {
          setBusy(true);
          try {
            const res = await axios.post(`/api/lockers/${locker.id}/reissue-pin`);
            setNewPin(res.data.pin);
            toast.success('New PIN issued');
          } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to issue PIN');
          } finally {
            setBusy(false);
            setConfirm(null);
          }
        }
      }),
    extend: () =>
      run(async () => {
        if (!occupant) throw new Error('no occupant');
        const base = occupant.valid_until && new Date(occupant.valid_until) > new Date()
          ? new Date(occupant.valid_until)
          : new Date();
        const newExpiry = new Date(base.getTime() + expiryHours * 60 * 60 * 1000);
        await axios.put(`/api/users/${occupant.id}/extend-expiry`, {
          newExpiryDate: newExpiry.toISOString()
        });
      }, `Extended by ${expiryHours}h`)
  };

  const isOnline = locker?.last_heartbeat
    ? new Date(locker.last_heartbeat) > new Date(Date.now() - 5 * 60 * 1000)
    : false;
  const timeRemaining = occupant?.valid_until ? getTimeRemaining(occupant.valid_until) : null;

  return (
    <LockerDrawerContext.Provider value={{ openLocker, closeLockerDrawer: close }}>
      {children}

      {lockerId && (
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-label="Locker details">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={close} />
          <div className="relative w-full max-w-md h-full material-thick border-l border-white/10 shadow-sheet overflow-y-auto animate-[fade-up_0.3s_cubic-bezier(0.32,0.72,0,1)]">
            {loading || !locker ? (
              <div className="h-full flex items-center justify-center">
                <RefreshCw className="w-7 h-7 animate-spin text-primary-400" />
              </div>
            ) : (
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p className="text-footnote text-secondary mb-0.5">Locker</p>
                    <h2 className="text-largetitle text-white leading-none">{locker.name}</h2>
                  </div>
                  <button
                    onClick={close}
                    className="p-2 rounded-full text-secondary hover:text-white hover:bg-white/10 transition-colors"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Status row */}
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className={`status-badge status-${locker.status}`}>{locker.status}</span>
                  <span className={`status-badge ${isOnline ? 'status-available' : 'bg-white/10 text-secondary'}`}>
                    {isOnline ? 'Online' : 'Offline'}
                  </span>
                  {group && (
                    <span className="status-badge" style={{ background: `${group.color}26`, color: group.color }}>
                      {group.name}
                    </span>
                  )}
                </div>

                {/* Facts */}
                <div className="material-thin rounded-2xl divide-y divide-white/[0.06] mb-6">
                  <div className="flex items-center px-4 py-3 space-x-3">
                    <MapPin className="w-4 h-4 text-secondary flex-shrink-0" />
                    <span className="text-footnote text-secondary flex-1">Location</span>
                    <span className="text-subheadline text-white">{locker.location || '-'}</span>
                  </div>
                  <div className="flex items-center px-4 py-3 space-x-3">
                    <Cpu className="w-4 h-4 text-secondary flex-shrink-0" />
                    <span className="text-footnote text-secondary flex-1">Controller</span>
                    <span className="text-subheadline text-white font-mono">{locker.ip_address || '-'}</span>
                  </div>
                  <div className="flex items-center px-4 py-3 space-x-3">
                    <Clock className="w-4 h-4 text-secondary flex-shrink-0" />
                    <span className="text-footnote text-secondary flex-1">Last heartbeat</span>
                    <span className="text-subheadline text-white">
                      {locker.last_heartbeat ? new Date(locker.last_heartbeat).toLocaleTimeString() : 'Never'}
                    </span>
                  </div>
                </div>

                {/* Occupant */}
                {occupant ? (
                  <div className="material-thin rounded-2xl p-4 mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                          <UserIcon className="w-4 h-4 text-primary-300" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-subheadline font-semibold text-white truncate">
                            {occupant.first_name} {occupant.last_name}
                          </p>
                          <p className="text-caption text-secondary truncate">{occupant.email}</p>
                        </div>
                      </div>
                      <button
                        className="btn btn-tinted btn-sm flex-shrink-0"
                        onClick={() => {
                          close();
                          navigate(`/users?userId=${occupant.id}`);
                        }}
                      >
                        Profile
                      </button>
                    </div>
                    {timeRemaining && (
                      <div className="flex items-center justify-between">
                        <span className="text-footnote text-secondary">Expires</span>
                        <div className="flex items-center space-x-2">
                          <span className={`text-subheadline font-medium ${
                            timeRemaining.expired ? 'text-danger-400' :
                            isExpiryApproaching(occupant.valid_until) ? 'text-warning-400' : 'text-success-400'
                          }`}>
                            {timeRemaining.expired ? 'Expired' : `In ${timeRemaining.text}`}
                          </span>
                          <button className="btn btn-secondary btn-sm" onClick={actions.extend} disabled={busy}>
                            +{expiryHours}h
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="material-thin rounded-2xl p-4 mb-6 text-center">
                    <p className="text-subheadline text-secondary">No guest assigned</p>
                  </div>
                )}

                {/* Fresh PIN result */}
                {newPin && (
                  <div className="material-regular border-primary-500/30 rounded-2xl p-5 mb-6 text-center animate-scale-in">
                    <p className="text-footnote text-label mb-1">New phone-unlock PIN</p>
                    <p className="text-5xl font-extrabold tracking-[0.3em] text-primary-300" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {newPin}
                    </p>
                    <p className="text-caption text-muted mt-2">
                      Show this to the guest now - it is not stored in plaintext.
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="space-y-2.5">
                  <button
                    className="btn btn-primary w-full"
                    onClick={actions.unlock}
                    disabled={busy || locker.status === 'maintenance'}
                  >
                    <Unlock className="w-4 h-4 mr-2" /> Open locker
                  </button>
                  {occupant && (
                    <button className="btn btn-tinted w-full" onClick={actions.reissuePin} disabled={busy}>
                      <KeyRound className="w-4 h-4 mr-2" /> Re-issue PIN
                    </button>
                  )}
                  <div className="grid grid-cols-2 gap-2.5">
                    <button className="btn btn-secondary" onClick={actions.maintenance} disabled={busy}>
                      <Wrench className="w-4 h-4 mr-2" />
                      {locker.status === 'maintenance' ? 'In service' : 'Maintenance'}
                    </button>
                    {occupant && (
                      <button className="btn btn-danger" onClick={actions.unassign} disabled={busy}>
                        <UserX className="w-4 h-4 mr-2" /> Unassign
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.action}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.label}
        tone={confirm?.tone}
        busy={busy}
      />
    </LockerDrawerContext.Provider>
  );
};

export default LockerDrawerContext;
